import type { ReactNode } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Pill } from '~/components/pill';
import { ActionCard } from './action-card';
import { ActionBody } from './action-body';
import { createAction } from '~/lib/schemas/action-defaults';
import { cn } from '@/lib/utils';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

function SortableActionItem({ id, children }: { id: string; children: (handleProps: Record<string, unknown>) => ReactNode }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
	const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined };
	return (
		<div ref={setNodeRef} style={style} className={cn(isDragging && 'opacity-80')}>
			{children({ ...attributes, ...listeners })}
		</div>
	);
}

/** Tab 3 — Ενέργειες: drag-orderable stack of action cards + add CTA. */
export function ActionsStack() {
	const { t } = useTranslation();
	const { control } = useFormContext<PromotionFormValues>();
	const { fields, append, remove, move } = useFieldArray({ control, name: 'actions' });
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			const oldIndex = fields.findIndex(f => f.id === active.id);
			const newIndex = fields.findIndex(f => f.id === over.id);
			if (oldIndex !== -1 && newIndex !== -1) move(oldIndex, newIndex);
		}
	};

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center justify-between rounded-lg border border-border bg-surface p-4 px-5">
				<div>
					<h2 className="text-md font-semibold text-foreground">{t('promotion.action.title')}</h2>
					<p className="mt-0.5 text-xs text-foreground-muted">{t('promotion.action.subtitle')}</p>
				</div>
				<Pill variant="primary">{t('promotion.action.count', { count: fields.length })}</Pill>
			</div>

			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
				<SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
					<div className="flex flex-col gap-3">
						{fields.map((field, index) => (
							<SortableActionItem key={field.id} id={field.id}>
								{handleProps => (
									<Controller
										control={control}
										name={`actions.${index}`}
										render={({ field: rhf }) => (
											<ActionCard
												index={index + 1}
												actionType={rhf.value.type}
												onTypeChange={type => rhf.onChange(createAction(type, rhf.value.combineWithOthers))}
												combineWithOthers={rhf.value.combineWithOthers}
												onCombineChange={v => rhf.onChange({ ...rhf.value, combineWithOthers: v })}
												onRemove={() => remove(index)}
												onDuplicate={() => append(rhf.value)}
												dragHandleProps={handleProps}>
												<ActionBody value={rhf.value} onChange={rhf.onChange} />
											</ActionCard>
										)}
									/>
								)}
							</SortableActionItem>
						))}
					</div>
				</SortableContext>
			</DndContext>

			<button
				type="button"
				onClick={() => append(createAction('percentage-discount'))}
				className={cn(
					'flex w-full items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-dashed border-border-strong p-4 text-sm text-foreground-muted',
					'transition-colors hover:border-brand hover:bg-brand-subtle hover:text-brand-subtle-fg'
				)}>
				<Plus size={16} /> {t('promotion.action.addAction')}
			</button>
		</div>
	);
}
